import React, { useState, useRef, useEffect } from 'react';
import { looksLikePHI, PHI_MESSAGE } from '../utils/phi';

const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

function loadTurnstileScript() {
  if (document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`)) {
    return;
  }
  const script = document.createElement('script');
  script.src = TURNSTILE_SCRIPT_SRC;
  script.async = true;
  document.head.appendChild(script);
}

const ContactFormFields = ({ defaultInquiryType }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });
  const [inquiryType, setInquiryType] = useState(defaultInquiryType);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [noPhiConfirmed, setNoPhiConfirmed] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');

  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (widgetIdRef.current) {
      return;
    }

    // Skip the widget during react-snap's prerender crawl: the DOM Turnstile
    // injects (with a random id) would be baked into the snapshot, so the
    // client's first render could never match it when hydrating.
    if (navigator.userAgent === 'ReactSnap') {
      return;
    }

    const renderWidget = () => {
      if (!window.turnstile || !turnstileRef.current || widgetIdRef.current) {
        return;
      }
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: turnstileSiteKey,
        action: 'contact_form',
        callback: (token) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileToken('')
      });
    };

    if (window.turnstile) {
      renderWidget();
      return;
    }

    loadTurnstileScript();

    // The Turnstile script tag is loaded async, so it can still be
    // mid-download when this component mounts. Poll until it's ready
    // instead of relying on a one-shot check that silently never renders
    // the widget if lost.
    const pollId = setInterval(() => {
      if (window.turnstile) {
        clearInterval(pollId);
        renderWidget();
      }
    }, 100);

    return () => clearInterval(pollId);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (honeypot) {
      return;
    }
    if (looksLikePHI(formData.message, formData.organization)) {
      setSubmitStatus('error');
      setErrorMessage(PHI_MESSAGE);
      return;
    }
    if (!noPhiConfirmed) {
      setSubmitStatus('error');
      setErrorMessage('Please confirm that your message contains no patient information.');
      return;
    }
    if (!turnstileToken) {
      setSubmitStatus('error');
      setErrorMessage('Please wait a moment for the verification check to finish, then try again.');
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, inquiryType, noPhiConfirmed, turnstileToken })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', organization: '', message: '' });
        setNoPhiConfirmed(false);
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'generate_lead', { inquiry_type: inquiryType });
        }
      } else {
        let detail = 'There was a problem sending your message. Call the lab at (561) 638-7577.';
        try {
          const data = await response.json();
          if (data.errors && data.errors[0] && data.errors[0].msg) {
            detail = data.errors[0].msg;
          } else if (data.error) {
            detail = data.error;
          }
        } catch (parseError) {
          // keep default detail
        }
        setErrorMessage(detail);
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('We could not reach the lab just now. Call (561) 638-7577 or try again in a few minutes.');
    }

    // Turnstile tokens are single-use, so reset the widget after every
    // attempt (success or failure) to get a fresh one for the next submit.
    setTurnstileToken('');
    if (window.turnstile && widgetIdRef.current) {
      window.turnstile.reset(widgetIdRef.current);
    }
    setIsSubmitting(false);
  };

  return (
    <div className="contact-form">
      <form onSubmit={handleSubmit} noValidate>
        <div className="inquiry-toggle" role="group" aria-label="What are you contacting us about?">
          <button
            type="button"
            className={`jump-chip${inquiryType === 'general' ? ' is-active' : ''}`}
            aria-pressed={inquiryType === 'general'}
            onClick={() => setInquiryType('general')}
          >
            General / Practice Question
          </button>
          <button
            type="button"
            className={`jump-chip${inquiryType === 'new-practice' ? ' is-active' : ''}`}
            aria-pressed={inquiryType === 'new-practice'}
            onClick={() => setInquiryType('new-practice')}
          >
            Become a Referring Practice
          </button>
        </div>
        <div className="form-grid">
          <div className="form-item">
            <label htmlFor="name">Name *</label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              autoComplete="name"
              required
              minLength={2}
            />
          </div>
          <div className="form-item">
            <label htmlFor="email">Email *</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
              required
            />
          </div>
          <div className="form-item full-width">
            <label htmlFor="organization">Organization *</label>
            <input
              id="organization"
              name="organization"
              type="text"
              value={formData.organization}
              onChange={handleChange}
              autoComplete="organization"
              required
              minLength={2}
            />
          </div>
          <div className="form-item full-width">
            <label htmlFor="message">Message *</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              minLength={10}
              maxLength={1000}
            />
          </div>
        </div>
        <input
          type="text"
          name="website"
          className="visually-hidden"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
        <label className="phi-confirm">
          <input
            type="checkbox"
            checked={noPhiConfirmed}
            onChange={(e) => setNoPhiConfirmed(e.target.checked)}
          />
          <span>This message contains no patient information (names, dates of birth, record numbers, or specimen IDs).</span>
        </label>
        <div className="turnstile-widget" ref={turnstileRef}></div>
        <p className="privacy-note">
          Use this form for practice questions only. Do not include patient names, dates of birth, specimen IDs, or other protected health information. We use the details you enter only to reply to this inquiry.
        </p>
        <button type="submit" className="cta-button" disabled={isSubmitting}>
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </button>
      </form>
      <div aria-live="polite">
        {submitStatus === 'success' && (
          <div className="alert success">
            <p>Your message has been sent. We will get back to you soon.</p>
          </div>
        )}
        {submitStatus === 'error' && (
          <div className="alert error">
            <p>{errorMessage}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const ContactForm = ({ defaultInquiryType = 'general' }) => {
  if (!turnstileSiteKey) {
    return (
      <p className="form-fallback">
        The web form is unavailable. Please call the laboratory at <a href="tel:+15616387577">(561) 638-7577</a>.
      </p>
    );
  }

  return <ContactFormFields defaultInquiryType={defaultInquiryType} />;
};

export default ContactForm;
